import * as icons from '../../../assets/icons'
import { Box, Button, Stack, TextArea, TextField } from '@dtx-company/design-system/src'
import { Cancel, HourglassTop } from '@mui/icons-material'
import { DESCRIPTION, LINK, TITLE } from './constants'
import { DESCRIPTION_MAX, DESCRIPTION_MAX_ERROR } from '../validation'
import { FC, useMemo } from 'react'
import { FormFields } from '../types'
import { FormProps, StyledFormV2 } from '../components/Layout'
import { FormTitle } from '../components/Title'
import { ImageDisplayToggle } from './ImageDisplayToggle'
import { ImageEdit } from '../components/ImageEdit'
import { LinkProvider } from '@dtx-company/inter-app/src/constants/linkTypes'
import { LinkStyleGated } from '../components/LinkStyle/LinkStyleGated'
import { SettingsAndSaveLink } from '../components/Settings'
import { logoUrlForType } from '../../../utils/main'
import { useDestinationFormState } from './hooks'
import { useRestrictedFlowpageLinkTypes } from '@app/code/src/components/utils/useRestrictedFlowpageLinkTypes'
import { validateLink } from '../utils'
import Image from 'next/image'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowpage'

// Edit a standard Link /pages/<hash>

enum MaskType {
  CIRCLE = 'circle',
  SQUARE = 'square',
  RECTANGLE = 'rectangle',
  IPHONEX = 'iphonex',
  FEATURED = 'featured',
  NO_IMAGE = 'noImage'
}

export const StandardLinkForm: FC<FormProps> = props => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const curr = useMemo(() => props.curr, [])
  const {
    watchAll,
    onSubmit,
    setPreviewAndFormValue,
    fetchOgInfoFromLink,
    isFetchingOgInfo,
    clearOGInfo,
    cancelFetchOgInfoFromLink,
    errors,
    trigger,
    meta,
    id,
    setMeta,
    linkTheme,
    setLinkTheme,
    register,
    validateTitle
  } = useDestinationFormState({ ...props, curr, order: curr ? curr.order : props.order })
  const isEdit = Boolean(curr)

  // BLM or AAPI campaign links. Not to be confused with link.displayType = 'featured'
  const isFeaturedLink = curr?.provider === LinkProvider.FEATURE
  const restrictedLinks = useRestrictedFlowpageLinkTypes()

  const ImageDisplayType = useMemo(() => {
    const fallbackUrl =
      meta.provider === LinkProvider.LINK ? undefined : logoUrlForType(meta.provider, meta.type)
    const fileUploadUrl = watchAll.image ?? fallbackUrl

    const displayTypeIsNoImage = watchAll.displayType === 'noImage'
    const displayTypeIsFeaturedImage = watchAll.displayType === 'featured'

    return (
      <>
        <ImageDisplayToggle
          fetching={isFetchingOgInfo}
          displayType={watchAll.displayType}
          onChange={(_, type) => {
            setPreviewAndFormValue(FormFields.IMAGE, undefined)
            setPreviewAndFormValue(FormFields.DISPLAY_TYPE, type)
          }}
        />
        {displayTypeIsNoImage ? null : (
          <Stack direction="row" justifyContent="space-between" gap="16px">
            <ImageEdit
              imageUrl={fileUploadUrl}
              setValue={setPreviewAndFormValue}
              defaultImage={fallbackUrl}
              id={id}
              active={props.curr ? props.curr.active : true}
              maskType={watchAll.displayType === 'default' ? MaskType.SQUARE : MaskType.FEATURED}
              performAutomaticDefaultCrop={!isEdit}
            />
            {fileUploadUrl && (
              <Image
                alt=""
                src={fileUploadUrl}
                width={displayTypeIsFeaturedImage ? 200 : 200}
                height={displayTypeIsFeaturedImage ? 79 : 200}
              />
            )}
          </Stack>
        )}
      </>
    )
  }, [
    id,
    isEdit,
    isFetchingOgInfo,
    meta.provider,
    meta?.type,
    props.curr,
    setPreviewAndFormValue,
    watchAll.displayType,
    watchAll.image
  ])

  return (
    <StyledFormV2 onSubmit={onSubmit}>
      <Stack direction="column" sx={{ padding: '0 16px' }} gap={'16px'}>
        <Box>
          <FormTitle
            icon={<Image width={24} height={24} src={icons.linkBlack} alt="" />}
            title={`${isEdit ? 'Edit' : 'Add'} a standard link`}
          />
        </Box>

        <Box>
          <TextField
            {...register(LINK, {
              validate: value => {
                return validateLink(value, setMeta, false, restrictedLinks)
              }
            })}
            fullWidth
            labelAbove
            label="Link URL"
            placeholder="Enter a URL"
            value={watchAll.link}
            error={Boolean(errors?.link)}
            helperText={errors?.link?.message}
            disabled={isFetchingOgInfo}
            onBlur={() => {
              trigger(LINK)
              events.Flowpage_PageProfile_StandardLinkForm_Typed_LinkUrl({
                linkId: id,
                typedUrl: watchAll.link
              })
            }}
            onChange={e => {
              setPreviewAndFormValue(LINK, e.target.value, { shouldValidate: true })
            }}
            endAdornment={
              isFetchingOgInfo ? (
                <Button endIcon={Cancel} onClick={cancelFetchOgInfoFromLink}>
                  Cancel
                </Button>
              ) : (
                <Stack sx={{ marginRight: '4px' }} justifyContent="space-between">
                  {watchAll.title || watchAll.description || watchAll.image ? (
                    <Button onClick={clearOGInfo}>Clear Form</Button>
                  ) : (
                    <Button
                      disabled={!watchAll.link}
                      onClick={() => fetchOgInfoFromLink(watchAll.link)}
                    >
                      Autofill
                    </Button>
                  )}
                </Stack>
              )
            }
          />
        </Box>

        {ImageDisplayType}

        <Box>
          <TextField
            {...register(TITLE, {
              validate: value => validateTitle(value)
            })}
            value={watchAll.title}
            fullWidth
            labelAbove
            error={Boolean(errors?.title)}
            helperText={errors?.title?.message}
            onChange={e => setPreviewAndFormValue(TITLE, e.target.value, { shouldValidate: true })}
            placeholder={isFetchingOgInfo ? 'Fetching data from URL…' : 'Enter a link title'}
            label="Link Title"
            disabled={isFetchingOgInfo}
            endAdornment={
              isFetchingOgInfo ? <HourglassTop height="14px" width="14px" /> : undefined
            }
          />
        </Box>

        <Box>
          <TextArea
            {...register(DESCRIPTION, {
              maxLength: {
                value: DESCRIPTION_MAX,
                message: DESCRIPTION_MAX_ERROR
              }
            })}
            value={watchAll.description}
            fullWidth
            labelAbove
            error={Boolean(errors?.description)}
            helperText={
              <Stack direction="row">
                {errors?.description?.message ? <Box>{errors.description.message}</Box> : null}
                <Box sx={{ width: '100%', textAlign: 'right !important' }}>
                  {`${watchAll.description.length} / ${DESCRIPTION_MAX}`}
                </Box>
              </Stack>
            }
            onChange={e =>
              setPreviewAndFormValue(DESCRIPTION, e.target.value, { shouldValidate: true })
            }
            placeholder={isFetchingOgInfo ? 'Fetching data from URL…' : 'Enter a link description'}
            label="Link Description (optional)"
            disabled={isFetchingOgInfo}
            endAdornment={
              isFetchingOgInfo ? <HourglassTop height="14px" width="14px" /> : undefined
            }
            inputProps={{ maxLength: DESCRIPTION_MAX }}
          />
        </Box>
        <Box>
          {!isFeaturedLink && <LinkStyleGated linkTheme={linkTheme} setLinkTheme={setLinkTheme} />}
        </Box>
        <Box>
          <SettingsAndSaveLink curr={curr} handleClose={props.handleClose} />
        </Box>
      </Stack>
    </StyledFormV2>
  )
}
